import { create } from "zustand";
import { axiosInstance } from "../lib/axios";
import toast from "react-hot-toast";

export type ActivityStatus = "Planned" | "In Progress" | "Completed" | "On Hold";
export type ActivityPriority = "Low" | "Medium" | "High";

export interface ActivityAttachment {
  name?: string;
  url?: string;
  type?: string;
}

export interface ActivityTask {
  _id: string;
  category: { _id: string; name: string; color?: string } | string;
  title: string;
  description: string;
  aiEnhancedDescription: string;
  priority: ActivityPriority;
  estimatedMinutes: number;
  dueDate: string | null;
  relatedLead: { _id: string; name: string; company?: string } | null;
  relatedProject: { _id: string; name: string } | null;
  relatedJob: { _id: string; jobTitle?: string } | null;
  relatedProposal: { _id: string; title?: string } | null;
  attachments: ActivityAttachment[];
  status: ActivityStatus;
  totalSeconds: number;
  elapsedSeconds: number;
  runningSince: string | null;
  notes: string;
  meetingMinutes?: { summary?: unknown; refinedAt?: string | null; sentToClient?: boolean } | null;
  createdAt: string;
  updatedAt: string;
}

export interface AiEnhanceResult {
  title: string;
  enhancedDescription: string;
  estimatedMinutes: number;
}

interface ActivityTaskState {
  activities: ActivityTask[];
  // When `activities` was last set — anchor for skew-free elapsed display.
  activitiesUpdatedAt: number;
  loading: boolean;
  saving: boolean;
  enhancing: boolean;
  // Per-activity in-flight flag for start/stop/status (button loaders).
  pending: Record<string, boolean>;

  fetchActivities: (params?: { status?: string; categoryId?: string }) => Promise<void>;
  syncActivities: () => Promise<void>;
  createActivity: (form: FormData) => Promise<ActivityTask | null>;
  deleteActivity: (id: string) => Promise<void>;
  startActivity: (id: string) => Promise<void>;
  stopActivity: (id: string) => Promise<void>;
  changeStatus: (id: string, status: ActivityStatus) => Promise<void>;
  enhanceWithAI: (description: string, categoryName?: string, context?: EnhanceContext) => Promise<AiEnhanceResult | null>;
}

// Extra record context passed to the AI so titles/descriptions can reference the
// real project / client / website / job instead of staying generic.
export interface EnhanceContext {
  projectName?: string;
  clientName?: string;
  website?: string;
  jobTitle?: string;
  proposalTitle?: string;
}

const replace = (list: ActivityTask[], updated: ActivityTask) =>
  list.map((a) => (a._id === updated._id ? updated : a));

// Drop any duplicate activities by _id (defensive against races).
const dedupe = (list: ActivityTask[]) => {
  const seen = new Set<string>();
  return list.filter((a) =>
    seen.has(a._id) ? false : (seen.add(a._id), true)
  );
};

export const useActivityTaskStore = create<ActivityTaskState>((set, get) => ({
  activities: [],
  activitiesUpdatedAt: Date.now(),
  loading: false,
  saving: false,
  enhancing: false,
  pending: {},

  fetchActivities: async (params) => {
    set({ loading: true });
    try {
      const { data } = await axiosInstance.get("/activities", { params });
      set({ activities: dedupe(data.activities), activitiesUpdatedAt: Date.now() });
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to load activities");
    } finally {
      set({ loading: false });
    }
  },

  // Poll ERP and reconcile (ERP-side start/stop reflects in CRE). Silent — no
  // loading flag/toast so it doesn't flicker the UI on each interval tick.
  syncActivities: async () => {
    try {
      const { data } = await axiosInstance.get("/activities/sync");
      set({ activities: dedupe(data.activities), activitiesUpdatedAt: Date.now() });
    } catch {
      // ignore transient sync errors (ERP/network hiccups)
    }
  },

  createActivity: async (form) => {
    set({ saving: true });
    try {
      // Do NOT set Content-Type manually — the browser must add the multipart
      // boundary itself, otherwise multer can't parse the body.
      const { data } = await axiosInstance.post("/activities", form);
      // Refetch the authoritative list (no optimistic prepend → no duplicate
      // flicker when a poll/refetch lands around the same time).
      await get().fetchActivities();
      toast.success("Activity created");
      return data.activity;
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to create activity");
      return null;
    } finally {
      set({ saving: false });
    }
  },

  deleteActivity: async (id) => {
    try {
      await axiosInstance.delete(`/activities/${id}`);
      set({
        activities: get().activities.filter((a) => a._id !== id),
        activitiesUpdatedAt: Date.now(),
      });
      toast.success("Activity deleted");
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to delete activity");
    }
  },

  startActivity: async (id) => {
    set((s) => ({ pending: { ...s.pending, [id]: true } }));
    try {
      await axiosInstance.post(`/activities/${id}/start`);
      // Refetch — starting one task auto-pauses any other running one.
      await get().fetchActivities();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to start timer");
    } finally {
      set((s) => {
        const p = { ...s.pending };
        delete p[id];
        return { pending: p };
      });
    }
  },

  stopActivity: async (id) => {
    set((s) => ({ pending: { ...s.pending, [id]: true } }));
    try {
      const { data } = await axiosInstance.post(`/activities/${id}/stop`);
      set({
        activities: replace(get().activities, data.activity),
        activitiesUpdatedAt: Date.now(),
      });
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to stop timer");
    } finally {
      set((s) => {
        const p = { ...s.pending };
        delete p[id];
        return { pending: p };
      });
    }
  },

  changeStatus: async (id, status) => {
    set((s) => ({ pending: { ...s.pending, [id]: true } }));
    try {
      const { data } = await axiosInstance.patch(`/activities/${id}/status`, {
        status,
      });
      set({
        activities: replace(get().activities, data.activity),
        activitiesUpdatedAt: Date.now(),
      });
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to update status");
    } finally {
      set((s) => {
        const p = { ...s.pending };
        delete p[id];
        return { pending: p };
      });
    }
  },

  enhanceWithAI: async (description, categoryName, context) => {
    set({ enhancing: true });
    try {
      const { data } = await axiosInstance.post("/activities/ai/enhance", {
        description,
        categoryName,
        context: context || undefined,
      });
      return data as AiEnhanceResult;
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "AI generation failed");
      return null;
    } finally {
      set({ enhancing: false });
    }
  },
}));
